import type { StandInDefinition } from "./adapter.ts";
import type { PixelBox } from "./prop-hit.ts";

/**
 * The editor's stand-in on the map (ADR-071 decisions 4 and 6): where its feet stand, in whole world pixels,
 * kept in a holder of its own whose listeners the renderer and the presence test subscribe to, so a drag or
 * a held arrow never notifies the store and re-renders no panel; its body box, which the Select tool hits
 * and the cover test reads; and the walk held arrows give it.
 */

/**
 * How fast held arrows walk a selected stand-in, in world pixels a second. Daedalus's own pace for moving
 * a view aid, not any game's walking speed.
 */
export const STAND_IN_SPEED = 120;

/**
 * A feet point in world pixels.
 */
export interface FeetPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Which arrows are held.
 */
export interface HeldArrows {
  readonly left: boolean;
  readonly right: boolean;
  readonly up: boolean;
  readonly down: boolean;
}

/**
 * One frame of a walk: the whole pixels to move and the fraction carried to the next frame.
 */
export interface WalkStep {
  readonly dx: number;
  readonly dy: number;
  readonly carry: FeetPoint;
}

/**
 * A point rounded to whole pixels and kept on a map, both edges included.
 *
 * @param point The point in world pixels.
 * @param width The map's width in pixels.
 * @param height The map's height in pixels.
 * @returns The clamped point.
 */
export function clampFeet(point: FeetPoint, width: number, height: number): FeetPoint {
  return { x: Math.min(width, Math.max(0, Math.round(point.x))), y: Math.min(height, Math.max(0, Math.round(point.y))) };
}

/**
 * The body box of a stand-in whose feet stand at a point: `width` by `height` pixels whose bottom centre is
 * the feet point, as the game's occluder fade places a character's body.
 *
 * @param feet The feet point, whole pixels.
 * @param standIn The registered stand-in.
 * @returns The box, every edge inclusive.
 */
export function standInBody(feet: FeetPoint, standIn: StandInDefinition): PixelBox {
  const x0 = feet.x - Math.floor(standIn.width / 2);
  return { x0, y0: feet.y - standIn.height, x1: x0 + standIn.width - 1, y1: feet.y - 1 };
}

/**
 * Whether a world point lies on a stand-in's body box, the area a Select press takes it by.
 *
 * @param point The point in world pixels, fractional.
 * @param feet The stand-in's feet point.
 * @param standIn The registered stand-in.
 * @returns True on the box.
 */
export function inStandInBody(point: FeetPoint, feet: FeetPoint, standIn: StandInDefinition): boolean {
  const body = standInBody(feet, standIn);
  return point.x >= body.x0 && point.x < body.x1 + 1 && point.y >= body.y0 && point.y < body.y1 + 1;
}

/**
 * One frame of held arrows: each axis moves `STAND_IN_SPEED` pixels a second by the frame's elapsed time,
 * whole pixels at a time, the fraction carried to the next frame so the pace holds at any frame rate. An
 * axis with no arrow held, or both, drops its carry.
 *
 * @param held The arrows held.
 * @param elapsedMs The frame's elapsed time in milliseconds.
 * @param carry The fraction carried from the last frame.
 * @returns The pixels to move and the new carry.
 */
export function walkStandIn(held: HeldArrows, elapsedMs: number, carry: FeetPoint): WalkStep {
  const step = (STAND_IN_SPEED * elapsedMs) / 1000;
  const axis = (minus: boolean, plus: boolean, carried: number): [number, number] => {
    const direction = (plus ? 1 : 0) - (minus ? 1 : 0);
    if (direction === 0) {
      return [0, 0];
    }
    const total = carried + direction * step;
    const whole = Math.trunc(total);
    return [whole, total - whole];
  };
  const [dx, cx] = axis(held.left, held.right, carry.x);
  const [dy, cy] = axis(held.up, held.down, carry.y);
  return { dx, dy, carry: { x: cx, y: cy } };
}

/**
 * Where the stand-in's feet stand, or null while it is not shown. View state with listeners of its own:
 * never saved, compiled, undone or passed through the store's notification.
 */
export class StandInFeet {
  private at: FeetPoint | null = null;
  private readonly listeners = new Set<() => void>();

  /**
   * The feet point.
   *
   * @returns The point, or null.
   */
  public get point(): FeetPoint | null {
    return this.at;
  }

  /**
   * Stands the feet at a point, rounded and clamped into a map, telling the listeners when it moved.
   *
   * @param point The point in world pixels.
   * @param width The map's width in pixels.
   * @param height The map's height in pixels.
   */
  public place(point: FeetPoint, width: number, height: number): void {
    this.set(clampFeet(point, width, height));
  }

  /**
   * Clamps the feet into a map again, after its size changed.
   *
   * @param width The map's width in pixels.
   * @param height The map's height in pixels.
   */
  public clampInto(width: number, height: number): void {
    if (this.at !== null) {
      this.set(clampFeet(this.at, width, height));
    }
  }

  /**
   * Forgets where the feet stood.
   */
  public clear(): void {
    this.set(null);
  }

  /**
   * Subscribes to moves.
   *
   * @param listener Called after the point changes.
   * @returns Unsubscribe function.
   */
  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Sets the point and tells the listeners when it changed.
   *
   * @param next The point, or null.
   */
  private set(next: FeetPoint | null): void {
    const at = this.at;
    if (at === next || (at !== null && next !== null && at.x === next.x && at.y === next.y)) {
      return;
    }
    this.at = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
